import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const HeroSection = () => {
  const { t } = useTranslation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Enhanced Overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1763280290224-1b7ed510a31e')`
          }} />

        {/* Dark overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 to-black/35" />
      </div>

      {/* Content */}
      <div className="container-cift relative z-10 text-center text-white py-32">
        <div className="max-w-4xl mx-auto">
          {/* Label - Updated */}
          <div className="mb-6">
            <span
              className="inline-block px-4 py-2 backdrop-blur-sm border text-sm font-semibold uppercase tracking-wider"
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                borderColor: 'rgba(255, 255, 255, 0.6)',
                color: '#ffffff'
              }}>

              {t('hero.badge')}
            </span>
          </div>

          {/* Main Headline - Enhanced readability */}
          <h1
            className="hero-title animate-fade-in-up mb-6 !text-[#FFFFFF]"
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>

            {t('hero.headline')}
          </h1>

          {/* Subheadline - Updated copy */}
          <p
            className="subtitle mb-12 max-w-2xl mx-auto animate-fade-in-up"
            style={{
              animationDelay: '0.2s',
              color: '#ffffff',
              textShadow: '0 1px 3px rgba(0,0,0,0.4)'
            }}>

            {t('hero.subheadline')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={() => scrollToSection('kontakt')}
              className="bg-[#1a1f2e] text-white border border-[#1a1f2e] hover:bg-[#2a2f3e] px-8 py-3 font-semibold text-sm uppercase tracking-wider transition-all flex items-center gap-2 group">

              {t('hero.cta.primary')}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => scrollToSection('portfolio')}
              className="bg-transparent border border-white text-white hover:bg-white hover:text-[#1a1f2e] px-8 py-3 font-semibold text-sm uppercase tracking-wider transition-all flex items-center gap-2">

              <Play size={20} />
              {t('hero.cta.secondary')}
            </button>
          </div>

          {/* Service Selection Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            {/* Business Card */}
            <button
              onClick={() => scrollToSection('business')}
              className="group relative overflow-hidden bg-white/10 backdrop-blur-md border border-white/20 p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105">

              <div className="text-left">
                <h3 className="text-2xl font-display font-bold mb-3 text-white">
                  {t('hero.card.business.title')}
                </h3>
                <p className="text-white/80 mb-4 text-sm leading-relaxed">
                  {t('hero.card.business.body')}
                </p>
                <span className="inline-flex items-center text-gold font-semibold text-sm group-hover:gap-2 transition-all">
                  {t('hero.card.business.link')}
                  <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>

            {/* Wedding Card */}
            <button
              onClick={() => scrollToSection('wedding')}
              className="group relative overflow-hidden bg-white/10 backdrop-blur-md border border-white/20 p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105">

              <div className="text-left">
                <h3 className="text-2xl font-display font-bold mb-3 text-white">
                  {t('hero.card.weddings.title')}
                </h3>
                <p className="text-white/80 mb-4 text-sm leading-relaxed">
                  {t('hero.card.weddings.body')}
                </p>
                <span className="inline-flex items-center text-gold font-semibold text-sm group-hover:gap-2 transition-all">
                  {t('hero.card.weddings.link')}
                  <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>);

};

export default HeroSection;